import type { BloodworkAnalysis } from './BloodworkAnalysis';
import type { PanelMarker } from './PanelMarker';

// ─── Markers Outside Optimal: system grouping ──────────────────────────────────
// The MOO ("Markers Outside Optimal") dashboard shows each out-of-optimal marker
// under a body-system header (e.g. "IRON STATUS", "LIVER"). That header is NOT
// stored on the marker — it is derived from which panel getter on
// BloodworkAnalysis lists the marker. A marker's reference-range bar legitimately
// appears in several sections, so many markers are listed in MORE THAN ONE panel
// getter (e.g. TIBC is in both the Iron and the Inflammation panels).
//
// This module owns the single source of truth for how markers map to MOO
// headers, so the web view (bloodwork-test-analysis.tsx) and the PDF export
// (bloodwork-analysis-pdf.tsx) can never drift apart. buildAttrToSystem resolves
// a marker's PRIMARY (home) section via "the primary section wins over a
// cross-cutting aggregator that only borrows it for context"; the MOO groups by
// that, so each system is ONE group. When a group's members are also relevant to
// a cross-cutting section (Inflammation / Glucose) in their out-of-optimal
// direction, appendSecondaryLabels widens that group's header — e.g. a Kidney
// group whose uric acid is high becomes "Kidney · Inflammation" — without
// splitting the group. Relevance is direction-aware (buildSecondaryTagger), so
// high TIBC never reads Inflammation while low TIBC does.

export interface SystemGroupDef {
  label: string;
  markers: PanelMarker[];
}

export interface SystemGroup {
  systemLabel: string;
  markers: PanelMarker[];
}

// Cross-cutting "aggregator" sections. They pull markers from the primary
// sections to tell a story (Inflammation borrows TIBC/ferritin/HDL/...; the
// Glucose add-on patterns borrow NLR/ALT/cortisol/...). They should label a
// marker only when NO primary section claims it (so hs-CRP/ESR/homocysteine
// still read "Inflammation", and glucose/HbA1c/insulin still read "Glucose").
export const AGGREGATOR_LABELS: ReadonlySet<string> = new Set(['Glucose', 'Inflammation']);

// Markers that live in TWO primary panels, where "last primary listed wins"
// (which correctly sends copper/zinc/vitamin A to Vitamins & Minerals) would
// pick the wrong home. Maps marker attribute → the header it must resolve to.
// Ceruloplasmin sits in both the Iron and the Vitamins & Minerals panels; it is
// grouped with the iron-utilization cluster, so it belongs under Iron Status.
export const PRIMARY_OVERRIDES: Readonly<Record<string, string>> = {
  ceruloplasmin: 'Iron Status',
};

// The out-of-optimal DIRECTION in which a marker is an Inflammation / oxidative-
// stress finding, mirroring InflammationOxidativeStressAnalysis. The Inflammation
// panel lists these markers unconditionally, so direction must be checked here to
// avoid mislabeling (only LOW TIBC is inflammatory, not high TIBC). Markers whose
// primary home is already Inflammation (hs-CRP, ESR, homocysteine) are omitted —
// they need no secondary tag. Glucose needs no such map: its add-on getters are
// already direction-gated, so Glucose membership is read straight from the panel.
export const INFLAMMATION_RELEVANT_DIRECTION: Readonly<Record<string, 'high' | 'low' | 'both'>> = {
  albumin: 'low',
  bilirubin: 'low',
  ceruloplasmin: 'high',
  ferritin: 'high',
  fibrinogen: 'high',
  ggt: 'high',
  hdl: 'high',
  tibc: 'low',
  transferrin: 'low',
  uricAcid: 'both',
};

// The ordered list of MOO system groups. Order matters: within the primary tier,
// a later panel overrides an earlier one (this is what sends copper/zinc/vitaminA
// to "Vitamins & Minerals" rather than "Iron"/"Immune"). Keep this in lockstep
// with the per-section panel getters on BloodworkAnalysis.
export function buildSystemGroupDefs(bloodworkAnalysis: BloodworkAnalysis): SystemGroupDef[] {
  return [
    { label: 'Blood Oxygen',        markers: bloodworkAnalysis.getRBCPanelMarkers?.() ?? [] },
    { label: 'Iron Status',         markers: bloodworkAnalysis.getIronPanelMarkers?.() ?? [] },
    { label: 'Immune',              markers: bloodworkAnalysis.getWBCPanelMarkers?.() ?? [] },
    { label: 'Blood Clotting',      markers: bloodworkAnalysis.getPlateletPanelMarkers?.() ?? [] },
    { label: 'Glucose',             markers: bloodworkAnalysis.getGlucosePanelMarkers?.() ?? [] },
    { label: 'Kidney',              markers: bloodworkAnalysis.getKidneyPanelMarkers?.() ?? [] },
    { label: 'Vitamins & Minerals', markers: bloodworkAnalysis.getMineralPanelMarkers?.() ?? [] },
    { label: 'Electrolytes',        markers: bloodworkAnalysis.getElectrolytePanelMarkers?.() ?? [] },
    { label: 'Proteins',            markers: bloodworkAnalysis.getProteinsPanelMarkers?.() ?? [] },
    { label: 'Liver',               markers: bloodworkAnalysis.getLiverPanelMarkers?.() ?? [] },
    { label: 'Lipids',              markers: bloodworkAnalysis.getLipidsPanelMarkers?.() ?? [] },
    { label: 'Inflammation',        markers: bloodworkAnalysis.getInflammationOxidativeStressPanelMarkers?.() ?? [] },
    { label: 'Thyroid',             markers: bloodworkAnalysis.getThyroidPanelMarkers?.() ?? [] },
    // Sodium/potassium stay labeled Electrolytes; only the ratio + adrenal
    // hormones carry the Stress & Adrenal label here.
    { label: 'Stress & Adrenal',    markers: (bloodworkAnalysis.getStressAdrenalPanelMarkers?.() ?? []).filter(pm => pm.attribute !== 'sodium' && pm.attribute !== 'potassium') },
    { label: 'Hormones',            markers: bloodworkAnalysis.getSexHormonePanelMarkers?.() ?? [] },
    { label: 'Prostate',            markers: bloodworkAnalysis.getProstatePanelMarkers?.() ?? [] },
  ];
}

// Resolve each marker attribute to exactly one MOO header.
//   pass 1 — aggregator sections set the fallback label;
//   pass 2 — primary sections overwrite it (later primary still beats earlier);
//   pass 3 — explicit overrides for markers split across two primary sections.
export function buildAttrToSystem(defs: SystemGroupDef[]): Record<string, string> {
  const attrToSystem: Record<string, string> = {};

  defs.forEach(({ label, markers }) => {
    if (!AGGREGATOR_LABELS.has(label)) return;
    markers.forEach(pm => { attrToSystem[pm.attribute] = label; });
  });

  defs.forEach(({ label, markers }) => {
    if (AGGREGATOR_LABELS.has(label)) return;
    markers.forEach(pm => { attrToSystem[pm.attribute] = label; });
  });

  Object.entries(PRIMARY_OVERRIDES).forEach(([attr, label]) => {
    if (attr in attrToSystem) attrToSystem[attr] = label;
  });

  return attrToSystem;
}

// Build a per-marker tagger for the cross-cutting (aggregator) sections a marker
// is relevant to GIVEN ITS CURRENT DIRECTION. The MOO groups markers by their
// primary home (buildAttrToSystem); these tags annotate a marker row with the
// other systems its out-of-optimal value speaks to — e.g. low TIBC → "Inflammation",
// uric acid (either direction) → "Inflammation", an ALT pulled into a glucose
// pattern → "Glucose". A marker whose primary home IS the aggregator, or that is
// out-of-optimal in a non-relevant direction (e.g. high TIBC), gets no tag.
export function buildSecondaryTagger(
  defs: SystemGroupDef[],
): (pm: PanelMarker) => string[] {
  const attrToSystem = buildAttrToSystem(defs);
  const glucoseAttrs = new Set(
    (defs.find(d => d.label === 'Glucose')?.markers ?? []).map(pm => pm.attribute),
  );

  return (pm: PanelMarker): string[] => {
    const primary = attrToSystem[pm.attribute];
    const tags: string[] = [];

    // Inflammation — only when out of optimal in the inflammation-relevant direction.
    if (primary !== 'Inflammation') {
      const dir = INFLAMMATION_RELEVANT_DIRECTION[pm.attribute];
      if (dir) {
        const isHigh = pm.isGreaterThanOptimal?.() === true;
        const isLow = pm.isStricklyLessThanOptimal?.() === true;
        const relevant = dir === 'both' ? (isHigh || isLow) : dir === 'high' ? isHigh : isLow;
        if (relevant) tags.push('Inflammation');
      }
    }

    // Glucose — the add-on getters already gate by direction, so presence in the
    // Glucose panel is itself the relevance signal.
    if (primary !== 'Glucose' && glucoseAttrs.has(pm.attribute)) {
      tags.push('Glucose');
    }

    return tags;
  };
}

// Group an out-of-optimal marker list by its primary system header, ordering the
// groups by their strongest (highest-scoring) marker so the most urgent system
// surfaces first. attrToSystem maps a marker attribute to its primary home (see
// buildAttrToSystem); cross-cutting relevance is shown per-marker via
// buildSecondaryTagger, not by splitting groups.
export function groupBySystem(
  markers: PanelMarker[],
  scoreOf: (pm: PanelMarker) => number,
  attrToSystem: Record<string, string>,
): SystemGroup[] {
  const groupMap: Record<string, PanelMarker[]> = {};
  const groupBestScore: Record<string, number> = {};
  markers.forEach(pm => {
    const label = attrToSystem[pm.attribute] ?? 'Other';
    const score = scoreOf(pm);
    if (!groupMap[label]) { groupMap[label] = []; groupBestScore[label] = score; }
    else if (score > groupBestScore[label]) { groupBestScore[label] = score; }
    groupMap[label].push(pm);
  });
  return Object.keys(groupMap)
    .sort((a, b) => groupBestScore[b] - groupBestScore[a])
    .map(label => ({ systemLabel: label, markers: groupMap[label] }));
}

// Separator between the primary system and its cross-cutting sections in a
// widened MOO header (e.g. "Kidney · Inflammation").
export const SECONDARY_LABEL_SEPARATOR = ' · ';

// Widen each group's header with the cross-cutting sections its members are
// relevant to (direction-aware, via a secondary tagger) — e.g. a Kidney group
// whose uric acid is out of optimal becomes "Kidney · Inflammation". Grouping
// stays by primary home (one group per system); only the header is widened. A
// section is appended when ANY member is relevant to it, so in a mixed group a
// non-relevant marker (e.g. high BUN) can sit under a header naming a section it
// is not itself part of.
export function appendSecondaryLabels(
  groups: SystemGroup[],
  secondaryTags: (pm: PanelMarker) => string[],
): SystemGroup[] {
  return groups.map(group => {
    const extra: string[] = [];
    group.markers.forEach(pm => {
      secondaryTags(pm).forEach(tag => { if (!extra.includes(tag)) extra.push(tag); });
    });
    return extra.length
      ? { ...group, systemLabel: [group.systemLabel, ...extra].join(SECONDARY_LABEL_SEPARATOR) }
      : group;
  });
}
